import React, { useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { apiClient } from '../../services/api';
import {
  channelUsesEmail,
  channelUsesSms,
  channelUsesWhatsApp,
  type NotificationChannelSelection,
} from '../../utils/notificationChannels';

export interface NotificationRecipientOption {
  userId: string;
  label: string;
  email: string | null;
  phoneNumber: string | null;
  role: string;
}

interface NotificationRecipientFieldsProps {
  channel: NotificationChannelSelection;
  emailRecipients: string;
  smsRecipients: string;
  onEmailRecipientsChange: (value: string) => void;
  onSmsRecipientsChange: (value: string) => void;
  defaultEmail?: string;
  defaultPhone?: string;
}

function splitRecipients(value: string): string[] {
  return value.split(',').map((part) => part.trim()).filter(Boolean);
}

function joinRecipients(values: string[]): string {
  return Array.from(new Set(values.filter(Boolean))).join(', ');
}

function formatPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

const NotificationRecipientFields: React.FC<NotificationRecipientFieldsProps> = ({
  channel,
  emailRecipients,
  smsRecipients,
  onEmailRecipientsChange,
  onSmsRecipientsChange,
  defaultEmail,
  defaultPhone,
}) => {
  const [options, setOptions] = useState<NotificationRecipientOption[]>([]);
  const showPhoneFields = channelUsesSms(channel) || channelUsesWhatsApp(channel);
  const phoneLabel = channelUsesSms(channel) && channelUsesWhatsApp(channel)
    ? 'SMS / WhatsApp phone'
    : channelUsesWhatsApp(channel)
      ? 'WhatsApp phone'
      : 'SMS phone';

  useEffect(() => {
    apiClient.notificationSettings.getRecipientOptions()
      .then((res) => setOptions(res.data.data || []))
      .catch(() => setOptions([]));
  }, []);

  useEffect(() => {
    if (defaultEmail && !emailRecipients) {
      onEmailRecipientsChange(defaultEmail);
    }
  }, [defaultEmail, emailRecipients, onEmailRecipientsChange]);

  useEffect(() => {
    if (defaultPhone && !smsRecipients) {
      onSmsRecipientsChange(defaultPhone);
    }
  }, [defaultPhone, smsRecipients, onSmsRecipientsChange]);

  const emailOptions = useMemo(() => options.filter((opt) => opt.email), [options]);
  const phoneOptions = useMemo(() => options.filter((opt) => opt.phoneNumber), [options]);

  const handleEmailPick = (userId: string) => {
    const picked = emailOptions.find((opt) => opt.userId === userId);
    if (!picked?.email) return;
    onEmailRecipientsChange(joinRecipients([...splitRecipients(emailRecipients), picked.email]));
  };

  const handlePhonePick = (userId: string) => {
    const picked = phoneOptions.find((opt) => opt.userId === userId);
    if (!picked?.phoneNumber) return;
    onSmsRecipientsChange(joinRecipients([...splitRecipients(smsRecipients), picked.phoneNumber]));
  };

  return (
    <Grid container spacing={2}>
      {channelUsesEmail(channel) && (
        <>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Select email recipient</InputLabel>
              <Select
                label="Select email recipient"
                defaultValue=""
                onChange={(e) => handleEmailPick(e.target.value)}
              >
                <MenuItem value="">
                  <em>Choose a manager / supervisor…</em>
                </MenuItem>
                {emailOptions.map((opt) => (
                  <MenuItem key={opt.userId} value={opt.userId}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              size="small"
              label="Email recipients"
              value={emailRecipients}
              onChange={(e) => onEmailRecipientsChange(e.target.value)}
              helperText="Comma-separated. Local dev redirects email to LOCAL_NOTIFICATION_EMAIL."
            />
          </Grid>
        </>
      )}

      {showPhoneFields && (
        <>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Select {phoneLabel}</InputLabel>
              <Select
                label={`Select ${phoneLabel}`}
                value={phoneOptions.find((opt) => opt.phoneNumber === smsRecipients.trim())?.userId || ''}
                onChange={(e) => handlePhonePick(e.target.value)}
              >
                <MenuItem value="">
                  <em>Choose a phone number…</em>
                </MenuItem>
                {phoneOptions.map((opt) => (
                  <MenuItem key={opt.userId} value={opt.userId}>
                    {opt.label} — {formatPhoneDisplay(opt.phoneNumber!)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {phoneOptions.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Add phone numbers under Profile → Notification Preferences, or type one below.
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              size="small"
              label={phoneLabel}
              placeholder="+12125551234 or 2105550100"
              value={smsRecipients}
              onChange={(e) => onSmsRecipientsChange(e.target.value)}
              helperText={
                channelUsesWhatsApp(channel)
                  ? 'Comma-separated. WhatsApp sandbox recipients must join the Twilio sandbox first.'
                  : 'Comma-separated. Local dev sends via Twilio when LOCAL_SMS_ENABLED=true.'
              }
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default NotificationRecipientFields;
